import React, { useState } from 'react';
import { 
  User, 
  Lock, 
  Building2, 
  Bell as BellIcon, 
  Shield, 
  ChevronLeft,
  Save,
  Eye,
  EyeOff,
  Check,
  Users,
  Plus,
  Trash2,
  Edit,
  Landmark,
  CreditCard,
  Receipt,
  Wallet,
  Monitor,
  Smartphone,
  Tablet as TabletIcon
} from 'lucide-react';
import { Card, Button, Input, Select } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useGet } from '../../hooks/useApi';
import { motion } from 'framer-motion';
import api from '../../utils/api';

const tabs = [
  { id: 'profile', label: 'المعلومات الشخصية', icon: User, description: 'الاسم والبريد الإلكتروني' },
  { id: 'password', label: 'كلمة المرور', icon: Lock, description: 'تغيير كلمة المرور' },
  { id: 'users', label: 'المستخدمين والصلاحيات', icon: Users, description: 'إدارة المستخدمين وصلاحياتهم' },
  { id: 'banks', label: 'إدارة البنوك', icon: Landmark, description: 'قائمة البنوك المعتمدة' },
  { id: 'bank-accounts', label: 'الحسابات البنكية', icon: CreditCard, description: 'أرقام الحسابات المتاحة' },
  { id: 'payment-modes', label: 'وسائل الدفع', icon: Receipt, description: 'طرق التحصيل (نقدا، شيك...)' },
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuth();

  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div>
        <h2 className="text-xl  text-slate-800">الإعدادات</h2>
        <p className="text-[11px] text-slate-400 leading-none mt-1">إدارة حسابك وتفضيلات النظام</p>
      </div>

      {/* Shopify-style Layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Side Navigation */}
        <div className="lg:w-56 shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-right transition-all duration-200
                    ${isActive 
                      ? 'bg-white border border-slate-200 shadow-sm text-slate-800' 
                      : 'text-slate-500 hover:bg-white/60 hover:text-slate-700'}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors
                    ${isActive ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'}`}>
                    <tab.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] truncate ${isActive ? '' : 'font-medium'}`}>{tab.label}</p>
                    <p className="text-[10px] text-slate-400 truncate">{tab.description}</p>
                  </div>
                  {isActive && <ChevronLeft className="w-4 h-4 text-slate-300 shrink-0" />}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {activeTab === 'profile' && <ProfileSection user={user} />}
            {activeTab === 'password' && <PasswordSection />}
            {activeTab === 'users' && <UsersSection />}
            { activeTab === 'banks' && <BanksSection />}
            { activeTab === 'bank-accounts' && <BankAccountsSection />}
            { activeTab === 'payment-modes' && <PaymentModesSection />}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Profile Section ──────────────────────────────
const ProfileSection = ({ user }) => {
  const { success, error } = useToast();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/user/profile', formData);
      success('تم تحديث المعلومات الشخصية بنجاح');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      error('خطأ أثناء حفظ البيانات');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <h3 className="text-sm  text-slate-800">المعلومات الشخصية</h3>
        <p className="text-[10px] text-slate-400 mt-0.5">تحديث اسمك وبريدك الإلكتروني</p>
      </div>
      <form onSubmit={handleSubmit} className="p-5 space-y-5">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-2xl  border-2 border-primary/20">
            {formData.name?.charAt(0) || 'م'}
          </div>
          <div>
            <p className="text-sm  text-slate-800">{formData.name || 'المسؤول'}</p>
            <p className="text-xs text-slate-400">{formData.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input 
            label="الاسم الكامل" 
            value={formData.name} 
            onChange={e => setFormData({...formData, name: e.target.value})} 
            placeholder="أدخل اسمك الكامل"
          />
          <Input 
            label="البريد الإلكتروني" 
            type="email"
            value={formData.email} 
            onChange={e => setFormData({...formData, email: e.target.value})} 
            placeholder="example@email.com"
          />
        </div>
        
        <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
          <Button type="submit" isLoading={loading}>
            {saved ? (
              <><Check className="w-4 h-4" /> تم الحفظ</>
            ) : (
              <><Save className="w-4 h-4" /> حفظ التغييرات</>
            )}
          </Button>
          {saved && (
            <motion.span 
              initial={{ opacity: 0, x: -10 }} 
              animate={{ opacity: 1, x: 0 }}
              className="text-xs text-emerald-600 "
            >
              ✓ تم التحديث بنجاح
            </motion.span>
          )}
        </div>
      </form>
    </Card>
  );
};

// ─── Password Section ──────────────────────────────
const PasswordInput = ({ label, field, showKey, showPasswords, setShowPasswords, formData, setFormData }) => (
  <div className="relative">
    <Input 
      label={label}
      type={showPasswords[showKey] ? 'text' : 'password'}
      value={formData[field]}
      onChange={e => setFormData({...formData, [field]: e.target.value})}
      placeholder="••••••••"
    />
    <button
      type="button"
      onClick={() => setShowPasswords({...showPasswords, [showKey]: !showPasswords[showKey]})}
      className="absolute left-3 top-8 text-slate-400 hover:text-slate-600 transition-colors"
    >
      {showPasswords[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  </div>
);

const PasswordSection = () => {
  const { success, error: toastError } = useToast();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.new_password !== formData.new_password_confirmation) {
      setError('كلمتا المرور الجديدتان غير متطابقتين');
      return;
    }
    if (formData.new_password.length < 6) {
      setError('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setLoading(true);
    try {
      await api.put('/user/password', formData);
      success('تم تغيير كلمة المرور بنجاح');
      setSaved(true);
      setFormData({ current_password: '', new_password: '', new_password_confirmation: '' });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      if (err.response?.status === 422) {
        setError('كلمة المرور الحالية غير صحيحة');
        toastError('كلمة المرور الحالية غير صحيحة');
      } else {
        toastError('خطأ أثناء تغيير كلمة المرور');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <h3 className="text-sm  text-slate-800">تغيير كلمة المرور</h3>
        <p className="text-[10px] text-slate-400 mt-0.5">تأكد من استخدام كلمة مرور قوية لحماية حسابك</p>
      </div>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-100 text-red-600 text-xs  px-4 py-3 rounded-lg"
          >
            {error}
          </motion.div>
        )}

        <PasswordInput 
          label="كلمة المرور الحالية" 
          field="current_password" 
          showKey="current" 
          showPasswords={showPasswords}
          setShowPasswords={setShowPasswords}
          formData={formData}
          setFormData={setFormData}
        />
        
        <div className="border-t border-slate-100 pt-4">
          <p className="text-[10px] text-slate-400  mb-3 uppercase tracking-wider">كلمة المرور الجديدة</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PasswordInput 
              label="كلمة المرور الجديدة" 
              field="new_password" 
              showKey="new" 
              showPasswords={showPasswords}
              setShowPasswords={setShowPasswords}
              formData={formData}
              setFormData={setFormData}
            />
            <PasswordInput 
              label="تأكيد كلمة المرور" 
              field="new_password_confirmation" 
              showKey="confirm" 
              showPasswords={showPasswords}
              setShowPasswords={setShowPasswords}
              formData={formData}
              setFormData={setFormData}
            />
          </div>
        </div>

        {/* Password strength hint */}
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
          <p className="text-[10px] text-slate-500  mb-2">متطلبات كلمة المرور:</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span className={`text-[10px] flex items-center gap-1 ${formData.new_password.length >= 6 ? 'text-emerald-600' : 'text-slate-400'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${formData.new_password.length >= 6 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              6 أحرف على الأقل
            </span>
            <span className={`text-[10px] flex items-center gap-1 ${/[A-Z]/.test(formData.new_password) ? 'text-emerald-600' : 'text-slate-400'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(formData.new_password) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              حرف كبير
            </span>
            <span className={`text-[10px] flex items-center gap-1 ${/[0-9]/.test(formData.new_password) ? 'text-emerald-600' : 'text-slate-400'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${/[0-9]/.test(formData.new_password) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              رقم واحد
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
          <Button type="submit" isLoading={loading}>
            {saved ? (
              <><Check className="w-4 h-4" /> تم التغيير</>
            ) : (
              <><Lock className="w-4 h-4" /> تحديث كلمة المرور</>
            )}
          </Button>
          {saved && (
            <motion.span 
              initial={{ opacity: 0, x: -10 }} 
              animate={{ opacity: 1, x: 0 }}
              className="text-xs text-emerald-600 "
            >
              ✓ تم تغيير كلمة المرور بنجاح
            </motion.span>
          )}
        </div>
      </form>
    </Card>
  );
};

// ─── Users Section ──────────────────────────────
const MODULES = [
  { id: 'dashboard', label: 'لوحة التحكم' },
  { id: 'students', label: 'الطلاب' },
  { id: 'school-classes', label: 'الأقسام' },
  { id: 'groups', label: 'المجموعات' },
  { id: 'institutions', label: 'المؤسسات' },
  { id: 'buses', label: 'الحافلات' },
  { id: 'drivers', label: 'السائقين' },
  { id: 'partners', label: 'الشركاء والموردين' },
  { id: 'staff', label: 'أعضاء الجمعية' },
  { id: 'regions', label: 'المناطق' },
  { id: 'academic-years', label: 'السنوات الدراسية' },
  { id: 'payments', label: 'المدفوعات' },
  { id: 'deposit-reports', label: 'تقارير الإيداع' },
  { id: 'bank', label: 'البنك (الودائع)' },
  { id: 'payment-modes', label: 'طرق الدفع' },
  { id: 'attendance', label: 'سجل الحضور' },
  { id: 'backup', label: 'النسخ الاحتياطي' },
];

const UsersSection = () => {
  const { success, error: toastError } = useToast();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { hasPermission, user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Admin';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    permissions: {},
    working_hours_start: '',
    working_hours_end: '',
    is_blocked: 'false'
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/users?page=${currentPage}`);
      // res.data is the paginated response since UserController returns response()->json($users)
      setUsers(res.data.data || []);
      setPagination(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [isAdmin, currentPage]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        permissions: user.permissions || {},
        working_hours_start: user.working_hours_start || '',
        working_hours_end: user.working_hours_end || '',
        is_blocked: user.is_blocked || 'false'
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        permissions: {},
        working_hours_start: '',
        working_hours_end: '',
        is_blocked: 'false'
      });
    }
    setShowModal(true);
  };

  const handleTogglePermission = (module, action) => {
    if (module === 'dashboard' && action !== 'read') return;
    
    setFormData(prev => {
      const modulePerms = prev.permissions[module] || { read: false, create: false, update: false, delete: false };
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: {
            ...modulePerms,
            [action]: !modulePerms[action]
          }
        }
      };
    });
  };

  const handleToggleAll = (action) => {
    setFormData(prev => {
      const allChecked = MODULES.every(m => {
        if (m.id === 'dashboard' && action !== 'read') return true;
        return prev.permissions[m.id]?.[action];
      });

      const newPerms = { ...prev.permissions };
      MODULES.forEach(m => {
        if (m.id === 'dashboard' && action !== 'read') return;
        newPerms[m.id] = {
          ...(newPerms[m.id] || { read: false, create: false, update: false, delete: false }),
          [action]: !allChecked
        };
      });

      return { ...prev, permissions: newPerms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, formData);
        success('تم تحديث المستخدم بنجاح');
      } else {
        await api.post('/users', formData);
        success('تم إضافة المستخدم بنجاح');
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Save failed', error);
      toastError('حدث خطأ أثناء حفظ المستخدم');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    try {
      await api.delete(`/users/${id}`);
      success('تم حذف المستخدم بنجاح');
      fetchUsers();
    } catch (error) {
      console.error(error);
      toastError('لا يمكن حذف هذا المستخدم');
    }
  };

  if (!isAdmin) {
    return (
      <Card className="p-10 text-center">
        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h3 className="text-sm  text-slate-800">صلاحيات غير كافية</h3>
        <p className="text-xs text-slate-500 mt-1">لا يمكنك الوصول لإدارة المستخدمين، هذا القسم مخصص للآدمن فقط.</p>
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden relative">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-sm  text-slate-800">إدارة المستخدمين</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">إضافة مستخدمين جدد وتحديد أذونات الوصول المخصصة لكل قسم</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="h-8 text-xs px-3">
          <Plus className="w-4 h-4 ml-1" /> إضافة مستخدم
        </Button>
      </div>
      
      <div className="p-5">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-50 rounded-xl" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {users.map(user => {
              const online = user.last_activity_at && (new Date() - new Date(user.last_activity_at)) < 60000;
              const DeviceIcon = user.last_device === 'Phone' ? Smartphone : (user.last_device === 'Tablet' ? TabletIcon : Monitor);
              
              return (
              <div key={user.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border border-slate-100 bg-white hover:border-primary/20 hover:shadow-sm transition-all duration-200">
                <div className="flex items-center gap-4 mb-3 sm:mb-0">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center  text-sm shrink-0">
                        {user.name.charAt(0)}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="text-sm  text-slate-800">{user.name}</h4>
                        {user.is_blocked === 'true' && <span className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0.5 rounded-full border border-red-100">محظور</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="text-[11px] text-slate-500">{user.email}</p>
                        {user.last_activity_at && (
                            <>
                                <span className="text-slate-200">•</span>
                                <div className="flex items-center gap-1 text-[10px] text-slate-400">
                                    <DeviceIcon className="w-3 h-3" />
                                    <span>{online ? 'متصل الآن' : 'نشط منذ قليل'}</span>
                                </div>
                            </>
                        )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-end gap-4">
                  <div className="text-[10px] text-slate-400 bg-slate-50 px-2.5 py-1 rounded hidden sm:block">
                    صلاحيات مخصصة
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleOpenModal(user)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    {!user.isAdmin && (
                      <button onClick={() => handleDelete(user.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
            
            {users.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm  text-slate-600">لا يوجد مستخدمين إضافيين</p>
              </div>
            )}

            {/* Pagination Controls */}
            {pagination && pagination.last_page > 1 && (
                <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between rounded-xl mt-4">
                    <div className="text-[10px] text-slate-500 font-normal">
                        عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> مستخدم
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="h-7 px-2 text-[10px]"
                        >
                            السابق
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                let pageNum = i + 1;
                                if (pagination.last_page > 5 && currentPage > 3) {
                                    pageNum = currentPage - 3 + i + 1;
                                    if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-7 h-7 rounded-lg text-[10px] transition-all ${
                                            currentPage === pageNum 
                                            ? 'bg-primary text-white shadow-sm font-medium' 
                                            : 'text-slate-500 hover:bg-slate-100'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="h-7 px-2 text-[10px]"
                        >
                            التالي
                        </Button>
                    </div>
                </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/20 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] relative my-auto"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white z-10">
              <h3 className=" text-slate-800">{editingUser ? 'تعديل مستخدم والصلاحيات' : 'إضافة مستخدم جديد'}</h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><EyeOff className="w-5 h-5 hidden" /> ✕</button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Input 
                  label="الاسم" 
                  value={formData.name} 
                  onChange={e => setFormData({...formData, name: e.target.value})} 
                  required 
                />
                <Input 
                  label="البريد الإلكتروني" 
                  type="email" 
                  value={formData.email} 
                  onChange={e => setFormData({...formData, email: e.target.value})} 
                  required 
                />
                <div className="md:col-span-2">
                  <Input 
                    label={editingUser ? "كلمة المرور (اتركها فارغة لعدم التغيير)" : "كلمة المرور"} 
                    type="password" 
                    value={formData.password} 
                    onChange={e => setFormData({...formData, password: e.target.value})} 
                    required={!editingUser} 
                  />
                </div>
                
                {/* Working Hours */}
                <div className="md:col-span-2 bg-slate-50/50 p-4 rounded-xl border border-slate-100 mb-2">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                                <Shield className="w-4 h-4" />
                            </div>
                            <div>
                                <h4 className="text-xs  text-slate-800">قيود الوصول</h4>
                                <p className="text-[10px] text-slate-400">تحديد ساعات العمل وحالة الحساب</p>
                            </div>
                        </div>
                        
                        {!editingUser?.isAdmin && (
                            <label className="inline-flex items-center cursor-pointer">
                                <span className="ml-2 text-xs  text-slate-600">حظر المستخدم</span>
                                <input 
                                    type="checkbox" 
                                    className="sr-only peer" 
                                    checked={formData.is_blocked === 'true'} 
                                    onChange={e => setFormData({...formData, is_blocked: e.target.checked ? 'true' : 'false'})} 
                                />
                                <div className="relative w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:end-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                            </label>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="وقت بدء العمل" 
                            type="time" 
                            value={formData.working_hours_start} 
                            onChange={e => setFormData({...formData, working_hours_start: e.target.value})} 
                        />
                        <Input 
                            label="وقت انتهاء العمل" 
                            type="time" 
                            value={formData.working_hours_end} 
                            onChange={e => setFormData({...formData, working_hours_end: e.target.value})} 
                        />
                    </div>
                    <p className="text-[9px] text-slate-400 mt-2 italic">* اترك الأوقات فارغة للسماح بالوصول في أي وقت.</p>
                </div>
              </div>
              
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                  <h4 className="text-xs  text-slate-700">تخصيص الصلاحيات</h4>
                  <p className="text-[10px] text-slate-500">حدد الأقسام التي تود تفعيلها لهذا المستخدم</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-right text-sm">
                    <thead>
                      <tr className="bg-slate-50/50 text-slate-500 text-[11px]">
                        <th className="font-medium p-3 border-b border-slate-100">القسم</th>
                        <th className="font-medium p-3 border-b border-slate-100 text-center w-20">
                          <div className="flex flex-col items-center gap-1 cursor-pointer" onClick={() => handleToggleAll('read')}>
                            <span>عرض</span>
                            <input type="checkbox" checked={MODULES.every(m => formData.permissions[m.id]?.read)} readOnly className="w-3 h-3 text-primary border-slate-300 rounded cursor-pointer" />
                          </div>
                        </th>
                        <th className="font-medium p-3 border-b border-slate-100 text-center w-20">
                          <div className="flex flex-col items-center gap-1 cursor-pointer" onClick={() => handleToggleAll('create')}>
                            <span>إضافة</span>
                            <input type="checkbox" checked={MODULES.filter(m => m.id !== 'dashboard').every(m => formData.permissions[m.id]?.create)} readOnly className="w-3 h-3 text-emerald-500 border-slate-300 rounded cursor-pointer" />
                          </div>
                        </th>
                        <th className="font-medium p-3 border-b border-slate-100 text-center w-20">
                          <div className="flex flex-col items-center gap-1 cursor-pointer" onClick={() => handleToggleAll('update')}>
                            <span>تعديل</span>
                            <input type="checkbox" checked={MODULES.filter(m => m.id !== 'dashboard').every(m => formData.permissions[m.id]?.update)} readOnly className="w-3 h-3 text-amber-500 border-slate-300 rounded cursor-pointer" />
                          </div>
                        </th>
                        <th className="font-medium p-3 border-b border-slate-100 text-center w-20">
                          <div className="flex flex-col items-center gap-1 cursor-pointer" onClick={() => handleToggleAll('delete')}>
                            <span>حذف</span>
                            <input type="checkbox" checked={MODULES.filter(m => m.id !== 'dashboard').every(m => formData.permissions[m.id]?.delete)} readOnly className="w-3 h-3 text-red-500 border-slate-300 rounded cursor-pointer" />
                          </div>
                        </th>
                        <th className="font-medium p-3 border-b border-slate-100 text-center w-20">ترحيل (المصروفات)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {MODULES.map(module => {
                        const perms = formData.permissions[module.id] || { read: false, create: false, update: false, delete: false };
                        return (
                          <tr key={module.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-3 font-medium text-slate-700 text-xs whitespace-nowrap">{module.label}</td>
                            
                            {/* Read Toggle */}
                            <td className="p-3 text-center">
                              <label className="inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={perms.read} onChange={() => handleTogglePermission(module.id, 'read')} />
                                <div className="relative w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:end-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                              </label>
                            </td>
                            
                            {/* Create Toggle */}
                            <td className="p-3 text-center">
                              {module.id !== 'dashboard' && (
                                <label className="inline-flex items-center cursor-pointer">
                                  <input type="checkbox" className="sr-only peer" checked={perms.create} onChange={() => handleTogglePermission(module.id, 'create')} />
                                  <div className="relative w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:end-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                                </label>
                              )}
                            </td>
                            
                            {/* Update Toggle */}
                            <td className="p-3 text-center">
                              {module.id !== 'dashboard' && (
                                <label className="inline-flex items-center cursor-pointer">
                                  <input type="checkbox" className="sr-only peer" checked={perms.update} onChange={() => handleTogglePermission(module.id, 'update')} />
                                  <div className="relative w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:end-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                                </label>
                              )}
                            </td>
                            
                            {/* Delete Toggle */}
                            <td className="p-3 text-center">
                              {module.id !== 'dashboard' && (
                                <label className="inline-flex items-center cursor-pointer">
                                  <input type="checkbox" className="sr-only peer" checked={perms.delete} onChange={() => handleTogglePermission(module.id, 'delete')} />
                                  <div className="relative w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:end-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                                </label>
                              )}
                            </td>
                            
                            {/* Transfer Expense Toggle */}
                            <td className="p-3 text-center">
                              {module.id === 'payments' && (
                                <label className="inline-flex items-center cursor-pointer">
                                  <input type="checkbox" className="sr-only peer" checked={perms.transfer_expense} onChange={() => handleTogglePermission(module.id, 'transfer_expense')} />
                                  <div className="relative w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:end-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-500"></div>
                                </label>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-6 mt-2">
                <Button type="button" variant="outline" className="px-6" onClick={() => setShowModal(false)}>إلغاء</Button>
                <Button type="submit" className="px-8">حفظ المستخدم</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </Card>
  );
};

// ─── Banks Section ──────────────────────────────
const BanksSection = () => {
  const { success, error } = useToast();
  const { data: banksResponse, loading, refresh } = useGet('/banks');
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const banks = Array.isArray(banksResponse) ? banksResponse : (banksResponse?.data || []);

  const handleOpenModal = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setFormData({ name: bank.name });
    } else {
      setEditingBank(null);
      setFormData({ name: '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBank) {
        await api.put(`/banks/${editingBank.id}`, formData);
        success('تم تحديث البنك بنجاح');
      } else {
        await api.post('/banks', formData);
        success('تم إضافة البنك بنجاح');
      }
      setShowModal(false);
      refresh();
    } catch (error) {
      error(error.response?.data?.message || 'خطأ أثناء الحفظ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا البنك؟')) return;
    try {
      await api.delete(`/banks/${id}`);
      success('تم حذف البنك بنجاح');
      refresh();
    } catch (error) {
      error(error.response?.data?.message || 'لا يمكن حذف هذا البنك');
    }
  };

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-sm text-slate-800">إدارة البنوك</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">قائمة البنوك المعتمدة لإيداعات المؤسسة</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="h-8 text-xs px-3">
          <Plus className="w-4 h-4 ml-1" /> إضافة بنك
        </Button>
      </div>
      
      <div className="p-5">
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-12 bg-slate-50 rounded-lg" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {banks.map(bank => (
              <div key={bank.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-primary/20 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Landmark size={14} /></div>
                  <span className="text-sm font-medium text-slate-700">{bank.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleOpenModal(bank)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(bank.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {banks.length === 0 && <p className="text-center py-8 text-xs text-slate-400 italic">لا توجد بنوك مسجلة</p>}
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingBank ? 'تعديل بنك' : 'إضافة بنك جديد'}>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <Input 
            label="اسم البنك" 
            value={formData.name} 
            onChange={e => setFormData({ name: e.target.value })} 
            placeholder="مثلاً: CIH Bank" 
            required 
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>إلغاء</Button>
            <Button type="submit">حفظ</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
};

// ─── Bank Accounts Section ──────────────────────────────
const BankAccountsSection = () => {
  const { success, error } = useToast();
  const { data: accountsResponse, loading, refresh } = useGet('/bank-accounts');
  const { data: banksResponse } = useGet('/banks');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({ account_number: '', bank_id: '' });
  
  const accounts = Array.isArray(accountsResponse) ? accountsResponse : (accountsResponse?.data || []);
  const banks = Array.isArray(banksResponse) ? banksResponse : (banksResponse?.data || []);

  const handleOpenModal = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({ account_number: account.account_number, bank_id: account.bank_id });
    } else {
      setEditingAccount(null);
      setFormData({ account_number: '', bank_id: banks.length > 0 ? banks[0].id : '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await api.put(`/bank-accounts/${editingAccount.id}`, formData);
        success('تم تحديث الحساب البنكي بنجاح');
      } else {
        await api.post('/bank-accounts', formData);
        success('تم إضافة الحساب البنكي بنجاح');
      }
      setShowModal(false);
      refresh();
    } catch (error) {
      error(error.response?.data?.message || 'خطأ أثناء الحفظ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الحساب؟')) return;
    try {
      await api.delete(`/bank-accounts/${id}`);
      success('تم حذف الحساب البنكي بنجاح');
      refresh();
    } catch (error) {
      error(error.response?.data?.message || 'لا يمكن حذف هذا الحساب');
    }
  };

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-sm text-slate-800">الحسابات البنكية</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">إدارة أرقام الحسابات البنكية الخاصة بالمؤسسة</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="h-8 text-xs px-3">
          <Plus className="w-4 h-4 ml-1" /> إضافة حساب
        </Button>
      </div>
      
      <div className="p-5">
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-12 bg-slate-50 rounded-lg" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map(acc => (
              <div key={acc.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-primary/20 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center"><CreditCard size={14} /></div>
                  <div>
                    <span className="text-sm font-bold text-slate-700 block">{acc.account_number}</span>
                    <span className="text-[10px] text-primary">{acc.bank?.name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleOpenModal(acc)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(acc.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {accounts.length === 0 && <p className="text-center py-8 text-xs text-slate-400 italic">لا توجد حسابات مسجلة</p>}
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingAccount ? 'تعديل حساب بنكي' : 'إضافة حساب بنكي جديد'}>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <Input 
            label="رقم الحساب" 
            value={formData.account_number} 
            onChange={e => setFormData({ ...formData, account_number: e.target.value })} 
            placeholder="000 000 000..." 
            required 
          />
          <Select
            label="البنك التابع له"
            options={banks.map(b => ({ value: b.id, label: b.name }))}
            value={formData.bank_id}
            onChange={e => setFormData({ ...formData, bank_id: e.target.value })}
            required
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>إلغاء</Button>
            <Button type="submit">حفظ</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
};


// ─── Payment Modes Section ──────────────────────────────
const PaymentModesSection = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const { data: modesResponse, refresh, loading } = useGet(`/payment-modes?page=${currentPage}`);
    
    const modes = modesResponse?.data || [];
    const pagination = modesResponse?.meta || null;

    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { success, error } = useToast();

    const handleAdd = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/payment-modes', { name, code });
            setName(''); 
            setCode(''); 
            success('تم إضافة وسيلة الدفع بنجاح');
            refresh();
        } catch (err) { 
            error('خطأ أثناء إضافة وسيلة الدفع'); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف وسيلة الدفع هذه؟')) {
            try {
                await api.delete(`/payment-modes/${id}`);
                success('تم الحذف بنجاح');
                refresh();
            } catch (err) {
                error('خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500 text-right">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h3 className="text-sm text-slate-800">وسائل الدفع</h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">إدارة طرق التحصيل المالي المتاحة (نقداً، شيك، تحويل...)</p>
                </div>
                <div className="flex bg-white px-3 py-1.5 rounded-lg border border-slate-100 items-center gap-2">
                    <Wallet className="text-primary w-4 h-4" />
                    <span className="text-xs text-slate-600">الوسائل المتوفرة: {pagination?.total || 0}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-5 h-fit lg:col-span-1 border-primary/10 bg-slate-50/50">
                    <h4 className="text-xs font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <Plus className="w-4 h-4 text-primary" /> تعريف وسيلة دفع
                    </h4>
                    <form onSubmit={handleAdd} className="space-y-4">
                        <Input 
                            label="كود الوسيلة" 
                            value={code} 
                            onChange={e => setCode(e.target.value)} 
                            placeholder="مثال: CASH, CHECK, BANK" 
                            required 
                        />
                        <Input 
                            label="اسم الوسيلة" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            placeholder="مثال: نقداً" 
                            required 
                        />
                        <Button type="submit" isLoading={isSubmitting} className="w-full mt-2 shadow-sm">
                            تعيين الوسيلة
                        </Button>
                    </form>
                </Card>

                <Card className="lg:col-span-2 overflow-hidden border-slate-100 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-slate-50/50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-3 text-[10px] text-slate-400 uppercase tracking-wider">الكود</th>
                                    <th className="px-6 py-3 text-[10px] text-slate-400 uppercase tracking-wider">اسم وسيلة الدفع</th>
                                    <th className="px-6 py-3 text-[10px] text-slate-400 uppercase tracking-wider text-left">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-10 text-center text-slate-400 animate-pulse text-xs">جاري جلب البيانات...</td>
                                    </tr>
                                ) : modes?.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-12 text-center text-slate-400">
                                            <p className="text-xs">لا توجد وسائل دفع معرفة حالياً</p>
                                        </td>
                                    </tr>
                                ) : (
                                    modes.map(mode => (
                                        <tr key={mode.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <span className="text-slate-600 bg-slate-100 px-2 py-0.5 rounded text-[10px] uppercase font-bold">
                                                    {mode.code}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded bg-primary/10 flex items-center justify-center text-primary">
                                                        <CreditCard size={12} />
                                                    </div>
                                                    <span className="text-xs text-slate-700">{mode.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-left">
                                                <button 
                                                    onClick={() => handleDelete(mode.id)} 
                                                    className="p-2 text-slate-300 hover:text-red-500 transition-colors bg-red-50/0 hover:bg-red-50 rounded-lg"
                                                    title="حذف الوسيلة"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.last_page > 1 && (
                        <div className="px-6 py-3 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                            <div className="text-[10px] text-slate-500 font-normal">
                                عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> وسيلة
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="h-7 px-2 text-[10px] shadow-none border-slate-200"
                                >
                                    السابق
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                    disabled={currentPage === pagination.last_page}
                                    className="h-7 px-2 text-[10px] shadow-none border-slate-200"
                                >
                                    التالي
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default SettingsPage;
